/**
 * §5.5 線上工作階段。需要 `session.manage`。
 *
 * 用途只有一個：**看誰在線上，並在需要時把人踢下線**。
 * 典型情境是「平板被留在現場沒登出」或「這個人已經離職但還在線上」。
 *
 * 強制登出只結束那一個工作階段，帳號本身不受影響——他下次照樣登得進來。
 * 要真的擋住人請用帳號管理的「停用」。這兩件事刻意分開：踢下線是當下的處置，
 * 停用是人事決定。
 */

import { useState } from 'react'
import { ApiError, api } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { useAuth } from '../../auth/AuthContext.js'
import { Alert, Button, Card, Empty, Mono, Pill, Table, Td } from '../../components/ui.js'
import { count, ts } from '../../../../shared/lib/format.js'

interface SessionRow {
  id: string
  user_id: number
  display_name: string
  email: string
  current_site_id: string | null
  created_at: string
  last_seen_at: string
  expires_at: string
  ip: string | null
  user_agent: string | null
}

export function AdminSessions() {
  const { me } = useAuth()
  const list = useQuery<{ items: SessionRow[] }>('/api/admin/sessions')
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const revoke = async (s: SessionRow) => {
    const self = s.user_id === me?.user.id
    const msg = self
      ? `這是您自己的工作階段。強制登出之後您會被登出，需要重新登入。\n\n確定要繼續嗎？`
      : `要把「${s.display_name}」從這個裝置登出嗎？\n\n他手上填到一半但還沒送出的資料會不見。\n帳號本身不受影響，他可以馬上再登入。`
    if (!confirm(msg)) return

    setBusy(s.id)
    try {
      await api.del(`/api/admin/sessions/${encodeURIComponent(s.id)}`)
      setNotice({ tone: 'ok', text: `已強制登出 ${s.display_name}，並寫入認證稽核。` })
      list.reload()
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(null) }
  }

  const items = list.data?.items ?? []
  // 同一個人可能同時在好幾台裝置上
  const people = new Set(items.map((s) => s.user_id)).size

  return (
    <div className="space-y-4">
      {notice && <Alert tone={notice.tone === 'ok' ? 'ok' : 'danger'}>{notice.text}</Alert>}

      <Card
        title="線上工作階段"
        subtitle={items.length > 0
          ? `${count(items.length)} 個工作階段、${count(people)} 個人（同一個人可能開了好幾台裝置）`
          : undefined}
        right={<Button size="sm" onClick={list.reload} disabled={list.loading}>重新整理</Button>}
      >
        {list.error && <Alert tone="danger" title="讀不到清單">{list.error.message}</Alert>}
        {items.length === 0 && !list.loading && <Empty>目前沒有人在線上。</Empty>}

        {items.length > 0 && (
          <Table head={['使用者', '目前廠區', '最後動作', '登入時間', '到期', '來源', '']}>
            {items.map((s) => {
              const self = s.user_id === me?.user.id
              return (
                <tr key={s.id} className={self ? 'bg-sky-50' : ''}>
                  <Td>
                    <span className="text-sm">{s.display_name}</span>
                    {self && <Pill tone="sky">這是您</Pill>}
                    <span className="block text-[11px] text-ink-400"><Mono>{s.email}</Mono></span>
                  </Td>
                  <Td className="text-xs">{s.current_site_id ?? <span className="text-ink-300">未選</span>}</Td>
                  <Td className="text-xs whitespace-nowrap">{ts(s.last_seen_at)}</Td>
                  <Td className="text-xs whitespace-nowrap">{ts(s.created_at)}</Td>
                  <Td className="text-xs whitespace-nowrap text-ink-500">{ts(s.expires_at)}</Td>
                  <Td className="text-xs">
                    <Mono className="text-[11px]">{s.ip ?? '—'}</Mono>
                    {s.user_agent && (
                      <span className="block text-[11px] text-ink-400" title={s.user_agent}>
                        {shortUA(s.user_agent)}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Button size="sm" variant="danger" disabled={busy === s.id}
                      onClick={() => void revoke(s)}>
                      {busy === s.id ? '處理中…' : '強制登出'}
                    </Button>
                  </Td>
                </tr>
              )
            })}
          </Table>
        )}
      </Card>

      <Card title="關於強制登出">
        <ul className="ml-4 list-disc space-y-1.5 text-sm text-ink-700">
          <li><strong>只結束這一個裝置上的登入。</strong>同一個人在別台裝置上的工作階段不受影響，要一起踢就每一列都按。</li>
          <li><strong>帳號本身不受影響</strong>，他下一秒就能再登入。要真的擋住人，到帳號管理按「停用」。</li>
          <li><strong>他手上沒送出的資料會不見</strong>——例如逐片秤填到一半。非緊急的話先跟他說一聲。</li>
          <li>這個動作會記進認證稽核（<Mono>session_revoked</Mono>），查得到是誰踢的。</li>
        </ul>
        <div className="mt-3">
          <Alert tone="info" title="有些情況不用手動踢">
            變更某人的角色、或停用他的帳號，系統會<strong>自動</strong>讓他所有的工作階段失效（§6.5）。
            所以人事異動照正常流程走就好，不需要再來這一頁。
          </Alert>
        </div>
      </Card>
    </div>
  )
}

/** 把 User-Agent 縮成看得懂的一句。認不出來就不猜，顯示「其他裝置」。 */
function shortUA(ua: string): string {
  const os = /Android/i.test(ua) ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua) ? 'iOS'
      : /Windows/i.test(ua) ? 'Windows'
        : /Mac OS X/i.test(ua) ? 'Mac'
          : /Linux/i.test(ua) ? 'Linux' : null
  const browser = /Edg\//i.test(ua) ? 'Edge'
    : /Vivaldi/i.test(ua) ? 'Vivaldi'
      : /OPR\//i.test(ua) ? 'Opera'
        : /Chrome\//i.test(ua) ? 'Chrome'
          : /Safari\//i.test(ua) ? 'Safari'
            : /Firefox\//i.test(ua) ? 'Firefox' : null
  if (!os && !browser) return '其他裝置'
  return [os, browser].filter(Boolean).join(' · ')
}
